export function normalizeCourseNameKey(name) {
  return String(name || "")
    .trim()
    .toLowerCase();
}

/** Keep one course per name (first provider alphabetically wins). */
export function deduplicateCoursesByName(courses = []) {
  const seen = new Set();
  const result = [];
  (courses || []).forEach((course) => {
    const key = normalizeCourseNameKey(course?.name || course?.label);
    if (!key || seen.has(key)) return;
    seen.add(key);
    result.push(course);
  });
  return result;
}

export function mapCourseFilterOptions(courses = []) {
  return (courses || [])
    .slice()
    .sort((a, b) => {
      const nameCompare = String(a.name || "").localeCompare(String(b.name || ""));
      if (nameCompare !== 0) return nameCompare;
      return String(a.providername || a.provider_name || "").localeCompare(
        String(b.providername || b.provider_name || ""),
      );
    })
    .map((course) => {
      const name = String(course.name || course.label || "").trim();
      const provider = String(
        course.providername || course.provider_name || "",
      ).trim();
      const country = String(course.country || "").trim();
      const labelParts = [name, provider, country].filter(Boolean);
      return {
        value: course.id ?? `${course.providerid}-${name}`,
        label: labelParts.join(" · "),
        data: course,
      };
    });
}

export const FEE_RANGES = [
  { value: "0 to 1000", label: "0 to 1,000", min: 0, max: 1000 },
  { value: "1001 to 5000", label: "1,001 to 5,000", min: 1001, max: 5000 },
  { value: "5001 to 10000", label: "5,001 to 10,000", min: 5001, max: 10000 },
  { value: "10001 to 20000", label: "10,001 to 20,000", min: 10001, max: 20000 },
  { value: "20001 to 35000", label: "20,001 to 35,000", min: 20001, max: 35000 },
  { value: "35,001+", label: "35,001+", min: 35001, max: null },
];

export function parseCampuses(cityStr) {
  if (!cityStr || typeof cityStr !== "string") return [];
  return cityStr
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

export function getValidIntakes(course) {
  const intakes = Array.isArray(course?.intakes) ? course.intakes : [];
  const seen = new Set();
  return intakes.filter((item) => {
    const label = String(item?.intake || "").trim();
    if (!label) return false;
    const key = String(item.intake_id ?? label);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function formatIntakeLabel(course) {
  const intakes = getValidIntakes(course);
  if (!intakes.length) return null;
  if (intakes.length === 1) return intakes[0].intake;
  return `${intakes[0].intake} +${intakes.length - 1} more`;
}

export function parseTuitionFee(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(String(value || "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

export function courseMatchesFeeRange(course, feeValue) {
  if (!feeValue) return true;
  const fee = parseTuitionFee(course?.tution_fee);
  if (fee === null) return true;
  const range = FEE_RANGES.find((item) => item.value === feeValue);
  if (!range) return true;
  if (range.max === null) return fee >= range.min;
  return fee >= range.min && fee <= range.max;
}

function uniqueSorted(values) {
  return [...new Set(values.filter(Boolean))].sort((a, b) =>
    String(a).localeCompare(String(b)),
  );
}

function getCourseDisciplines(course) {
  const list = [
    ...(Array.isArray(course?.disciplines) ? course.disciplines : []),
    course?.discipline,
  ];
  return uniqueSorted(list.map((item) => String(item).trim()).filter(Boolean));
}

function getCoursePrerequisites(course) {
  return uniqueSorted(
    (Array.isArray(course?.prerequisites) ? course.prerequisites : []).map((item) =>
      String(item).trim(),
    ),
  );
}

/** Courses with missing/null facet values still pass — they must not hide other filter options. */
function courseMatchesCampus(course, campus) {
  if (!campus) return true;
  const campuses = parseCampuses(course?.campus);
  if (!campuses.length) return true;
  const needle = String(campus).toLowerCase();
  return campuses.some((item) => item.toLowerCase().includes(needle));
}

function courseMatchesIntake(course, intakeId) {
  if (!intakeId) return true;
  const validIntakes = getValidIntakes(course);
  if (!validIntakes.length) return true;
  return validIntakes.some(
    (item) => String(item.intake_id) === String(intakeId),
  );
}

function courseMatchesLevel(course, level) {
  if (!level) return true;
  if (!course?.level) return true;
  return String(course.level) === String(level);
}

function courseMatchesDuration(course, duration) {
  if (!duration) return true;
  if (!course?.duration) return true;
  return String(course.duration) === String(duration);
}

function courseMatchesDiscipline(course, discipline) {
  if (!discipline) return true;
  const disciplines = getCourseDisciplines(course);
  if (!disciplines.length) return true;
  return disciplines.includes(discipline);
}

function courseMatchesPrerequisite(course, prerequisite) {
  if (!prerequisite) return true;
  const prerequisites = getCoursePrerequisites(course);
  if (!prerequisites.length) return true;
  return prerequisites.includes(prerequisite);
}

function courseMatchesProviders(course, providerIds) {
  if (!providerIds?.length) return true;
  const ids = new Set(providerIds.map((id) => String(id)));
  return ids.has(String(course?.providerid ?? ""));
}

function courseMatchesCountry(course, country) {
  if (!country) return true;
  return String(course?.country || "").trim() === String(country).trim();
}

export function buildCountrySuggestions(courses = []) {
  const counts = new Map();
  (courses || []).forEach((course) => {
    const country = String(course?.country || "").trim();
    if (!country) return;
    counts.set(country, (counts.get(country) || 0) + 1);
  });
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([country, count]) => ({ value: country, label: country, count }));
}

export function buildCountryFilterOptions(courses = [], staticCountries = []) {
  const fromData = buildCountrySuggestions(courses).map(({ value, label, count }) => ({
    value,
    label: `${label} (${count})`,
    rawLabel: label,
    count,
  }));
  if (fromData.length) return fromData;
  return (staticCountries || []).map((item) => ({
    value: item.value,
    label: item.value,
    rawLabel: item.value,
    count: 0,
  }));
}

export function filterCoursesForFacet(courses, filters, excludeKey) {
  const list = Array.isArray(courses) ? courses : [];
  return list.filter((course) => {
    if (excludeKey !== "country" && !courseMatchesCountry(course, filters.country)) {
      return false;
    }
    if (excludeKey !== "provider" && !courseMatchesProviders(course, filters.providerIds)) {
      return false;
    }
    if (excludeKey !== "discipline" && !courseMatchesDiscipline(course, filters.discipline)) {
      return false;
    }
    if (excludeKey !== "level" && !courseMatchesLevel(course, filters.level)) {
      return false;
    }
    if (excludeKey !== "duration" && !courseMatchesDuration(course, filters.duration)) {
      return false;
    }
    if (excludeKey !== "intake" && !courseMatchesIntake(course, filters.intakeId)) {
      return false;
    }
    if (excludeKey !== "campus" && !courseMatchesCampus(course, filters.campus)) {
      return false;
    }
    if (excludeKey !== "fee" && !courseMatchesFeeRange(course, filters.fee)) {
      return false;
    }
    if (
      excludeKey !== "prerequisite" &&
      !courseMatchesPrerequisite(course, filters.prerequisite)
    ) {
      return false;
    }
    return true;
  });
}

export function courseMatchesClientFilters(course, filters = {}) {
  if (!courseMatchesCountry(course, filters.country)) return false;
  if (!courseMatchesProviders(course, filters.providerIds)) return false;
  if (!courseMatchesDiscipline(course, filters.discipline)) return false;
  if (!courseMatchesLevel(course, filters.level)) return false;
  if (!courseMatchesDuration(course, filters.duration)) return false;
  if (!courseMatchesIntake(course, filters.intakeId)) return false;
  if (!courseMatchesCampus(course, filters.campus)) return false;
  if (!courseMatchesFeeRange(course, filters.fee)) return false;
  if (!courseMatchesPrerequisite(course, filters.prerequisite)) return false;
  return true;
}

export function mapApiFilterOptions(apiFilters = {}, staticCountries = []) {
  const filters = apiFilters || {};

  const countryOptions = (filters.countries || []).length
    ? (filters.countries || []).map((country) => ({
        value: country,
        label: country,
        rawLabel: country,
      }))
    : (staticCountries || []).map((item) => ({
        value: item.value,
        label: item.value,
        rawLabel: item.value,
      }));

  const providerOptions = (filters.providers || [])
    .slice()
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
    .map((provider) => {
      const country = String(provider.country || "").trim();
      const name = String(provider.name || "").trim();
      return {
        value: provider.id,
        label: country ? `${name} · ${country}` : name,
        data: provider,
      };
    });

  const disciplineOptions = (filters.disciplines || []).map((label) => ({
    value: label,
    label,
  }));

  const levelOptions = (filters.levels || []).map((label) => ({
    value: label,
    label,
  }));

  const durationOptions = (filters.durations || []).map((label) => ({
    value: label,
    label,
  }));

  const intakeOptions = (filters.intakes || []).map((item) => ({
    value: String(item.intake_id),
    label: item.intake,
    rawValue: item.intake_id,
  }));

  const campusOptions = (filters.campuses || []).map((label) => ({
    value: label,
    label,
  }));

  const feeOptions = FEE_RANGES.map((range) => ({
    value: range.value,
    label: range.label,
  }));

  const prerequisiteOptions = (filters.prerequisites || []).map((label) => ({
    value: label,
    label,
  }));

  const courseOptions = mapCourseFilterOptions(filters.courses || []);

  return {
    countryOptions,
    providerOptions,
    courseOptions,
    disciplineOptions,
    levelOptions,
    durationOptions,
    intakeOptions,
    campusOptions,
    feeOptions,
    prerequisiteOptions,
  };
}

export function buildCounselingFilterOptions({
  courses = [],
  providers = [],
  activeFilters = {},
  staticCountries = [],
}) {
  const countryPool = filterCoursesForFacet(courses, activeFilters, "country");
  const countryOptions = buildCountryFilterOptions(countryPool, staticCountries);

  const providerPool = filterCoursesForFacet(courses, activeFilters, "provider");
  const providerIdsInPool = new Set(
    providerPool.map((course) => String(course.providerid ?? "")),
  );

  const disciplinePool = filterCoursesForFacet(courses, activeFilters, "discipline");
  const levelPool = filterCoursesForFacet(courses, activeFilters, "level");
  const durationPool = filterCoursesForFacet(courses, activeFilters, "duration");
  const intakePool = filterCoursesForFacet(courses, activeFilters, "intake");
  const campusPool = filterCoursesForFacet(courses, activeFilters, "campus");
  const prerequisitePool = filterCoursesForFacet(
    courses,
    activeFilters,
    "prerequisite",
  );

  const providerOptions = (providers || [])
    .slice()
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
    .map((provider) => {
      const country = String(provider.country || "").trim();
      const name = String(provider.name || "").trim();
      return {
        value: provider.id,
        label: country ? `${name} · ${country}` : name,
        data: provider,
      };
    });

  const disciplineOptions = uniqueSorted(
    disciplinePool.flatMap((course) => getCourseDisciplines(course)),
  ).map((label) => ({ value: label, label }));

  const levelOptions = uniqueSorted(
    levelPool.map((course) => course.level).filter(Boolean),
  ).map((label) => ({ value: label, label }));

  const durationOptions = uniqueSorted(
    durationPool.map((course) => course.duration).filter(Boolean),
  ).map((label) => ({ value: label, label }));

  const intakeMap = new Map();
  intakePool.forEach((course) => {
    getValidIntakes(course).forEach((item) => {
      const key = String(item.intake_id);
      if (!intakeMap.has(key)) {
        intakeMap.set(key, {
          value: key,
          label: item.intake,
          rawValue: item.intake_id,
        });
      }
    });
  });
  const intakeOptions = [...intakeMap.values()].sort((a, b) =>
    String(a.label).localeCompare(String(b.label)),
  );

  const campusSet = new Set();
  const campusProviderPool = (providers || []).filter((provider) =>
    providerIdsInPool.has(String(provider.id ?? "")),
  );
  campusProviderPool.forEach((provider) => {
    parseCampuses(provider.city).forEach((campus) => campusSet.add(campus));
  });
  campusPool.forEach((course) => {
    parseCampuses(course.campus).forEach((campus) => campusSet.add(campus));
  });
  const campusOptions = [...campusSet]
    .sort((a, b) => a.localeCompare(b))
    .map((label) => ({ value: label, label }));

  const feeOptions = FEE_RANGES.map((range) => ({
    value: range.value,
    label: range.label,
  }));

  const prerequisiteOptions = uniqueSorted(
    prerequisitePool.flatMap((course) => getCoursePrerequisites(course)),
  ).map((label) => ({ value: label, label }));

  const courseOptions = mapCourseFilterOptions(
    (courses || []).map((course) => ({
      id: course.id,
      name: course.name,
      providerid: course.providerid,
      providername: course.providername,
      country: course.country,
    })),
  );

  return {
    countryOptions,
    providerOptions,
    courseOptions,
    disciplineOptions,
    levelOptions,
    durationOptions,
    intakeOptions,
    campusOptions,
    feeOptions,
    prerequisiteOptions,
  };
}

export function getFeeLabel(feeValue) {
  if (!feeValue) return "";
  return FEE_RANGES.find((item) => item.value === feeValue)?.label || feeValue;
}

export function buildActiveFilterChips({
  fee = "",
  level = "",
  prerequisite = "",
  discipline = "",
  country = "",
  intake = "",
  duration = "",
  campus = "",
  providers = [],
  courses = [],
}) {
  const chips = [];

  if (fee) {
    chips.push({
      key: "fee",
      label: getFeeLabel(fee),
      category: "Budget",
      onClear: { name: "tutionfee", value: "" },
    });
  }
  if (level) {
    chips.push({
      key: "level",
      label: level,
      category: "Level",
      onClear: { name: "levels", value: "" },
    });
  }
  if (prerequisite) {
    chips.push({
      key: "prerequisite",
      label: prerequisite,
      category: "English Test",
      onClear: { name: "prerequisit", value: "" },
    });
  }
  if (discipline) {
    chips.push({
      key: "discipline",
      label: discipline,
      category: "Discipline",
      onClear: { name: "discipline", value: "" },
    });
  }
  if (country) {
    chips.push({
      key: "country",
      label: country,
      category: "Country",
      onClear: { name: "countries", value: "" },
    });
  }
  (providers || []).forEach((provider) => {
    const id = provider.id ?? provider.value ?? provider.name;
    chips.push({
      key: `provider-${id}`,
      label: provider.name || provider.label || "Provider",
      category: "Provider",
      onClear: { name: "provider", value: id, type: "remove-provider" },
    });
  });
  (courses || []).forEach((course) => {
    const id = course.id ?? course.value;
    const label =
      course.label ||
      [course.name || course.label, course.providername, course.country]
        .filter(Boolean)
        .join(" · ") ||
      "Course";
    chips.push({
      key: `course-${id}`,
      label,
      category: "Course",
      onClear: { name: "course", value: id, type: "remove-course" },
    });
  });
  if (intake) {
    chips.push({
      key: "intake",
      label: intake,
      category: "Intake",
      onClear: { name: "intake", value: "", optionName: "" },
    });
  }
  if (duration) {
    chips.push({
      key: "duration",
      label: duration,
      category: "Duration",
      onClear: { name: "duration", value: "" },
    });
  }
  if (campus) {
    chips.push({
      key: "campus",
      label: campus,
      category: "Campus",
      onClear: { name: "campus", value: "" },
    });
  }

  return chips;
}

export function currencyPrefix(country) {
  if (country === "Australia") return "AUD";
  if (country === "Canada") return "CAD";
  if (country === "Uk" || country === "UK") return "GBP";
  if (country === "United States" || country === "USA") return "USD";
  if (country === "New Zealand") return "NZD";
  return "";
}

export function formatTuitionFee(course) {
  const fee = parseTuitionFee(course?.tution_fee);
  if (fee === null) return null;
  const prefix = currencyPrefix(course?.country);
  const formatted = fee.toLocaleString();
  return prefix ? `${prefix} ${formatted}` : formatted;
}

export function formatPathwayFeeLabel(course) {
  const min = parseTuitionFee(course?.fee_min ?? course?.tution_fee);
  const max = parseTuitionFee(course?.fee_max);
  const prefix = currencyPrefix(course?.country || course?.variant_countries?.[0]);

  if (min !== null && max !== null && min !== max) {
    const fmt = (value) => (prefix ? `${prefix} ${value.toLocaleString()}` : value.toLocaleString());
    return `${fmt(min)} – ${fmt(max)}`;
  }

  return formatTuitionFee(course);
}

export function getPathwayAvailabilityLabel(course) {
  const count = Number(course?.variant_count || 1);
  const countries = Array.isArray(course?.variant_countries)
    ? course.variant_countries.filter(Boolean)
    : [];
  const providers = Array.isArray(course?.variant_providers)
    ? course.variant_providers.filter(Boolean)
    : [];

  if (count <= 1) {
    return null;
  }

  const providerCount = providers.length || count;
  const countryCount = countries.length;

  if (countryCount > 1) {
    return `${providerCount} provider${providerCount === 1 ? "" : "s"} · ${countryCount} countries`;
  }

  return `${providerCount} provider${providerCount === 1 ? "" : "s"}`;
}
